import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import { GenericForm, GenericFormField, firstMissingRequiredMessage } from '@/styles/GenericForm';
import { GenericAttachmentsSection, type GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { StandardButton, SecondaryButton } from '@/styles/StandardButtons';
import { fetchAssignableUsers } from '../store';
import { Warranty, NewWarrantyInput, WarrantyStatus, User } from '../types';
import { STATUS_OPTIONS, MAX_FILE_BYTES } from '../constants';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { fetchEntityAttachments, uploadEntityAttachments, deleteAttachment } from '@/lib/entity-attachments';

interface WarrantyFormProps {
  mode: 'create' | 'edit';
  initialData?: Warranty | null;
  projects: { id: string; name: string }[];
  onSubmit: (data: NewWarrantyInput) => Promise<Warranty | undefined>;
  onCancel: () => void;
  isOpen: boolean;
  /** "Email to Client": emails the warranty (claim, response, status, latest file) to the project's client. */
  onSend?: (warrantyId: string, warrantyData?: Warranty) => Promise<void> | void;
  /** "Create PO": the page opens the purchase order form over this one. Receives the saved warranty. */
  onCreatePO?: (warranty: Warranty, projectId: string) => void;
  /** Something the table shows changed (comment, attachment): reload the list. */
  onChanged?: () => void;
  /** The client's view: it opens a claim (title, project, description) and then only reads it and comments; the rest belongs to the company. */
  clientView?: boolean;
}

export function WarrantyForm({ mode, initialData, projects, onSubmit, onCancel, isOpen, onSend, onCreatePO, onChanged, clientView = false }: WarrantyFormProps) {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  const warrantyId = mode === 'edit' ? initialData?.id : undefined;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [isLoadingAssignmentOptions, setIsLoadingAssignmentOptions] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});

  // -- attachments: the generic system (staged until the warranty exists) ------------
  const uploadFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    const uploaded = await uploadEntityAttachments('warranty', id, files, companyId, user.id);
    if (uploaded.length > 0) onChanged?.();
    return uploaded;
  }, [companyId, user?.id, onChanged]);
  const deleteFn = useCallback(async (attachmentId: string) => {
    const ok = await deleteAttachment(attachmentId, user?.id);
    if (ok) onChanged?.();
    return ok;
  }, [user?.id, onChanged]);
  const {
    pendingFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleUploadBase,
    handleRemovePending,
    handleDelete,
    resetPending,
    uploadPending,
  } = useAttachmentStaging<GenericAttachment>({ entityId: warrantyId, uploadFn, deleteFn, onError: setError });

  const handleUpload = useCallback(async (files: File[]) => {
    if (files.some((f) => f.size > MAX_FILE_BYTES)) {
      setError('One or more files exceed 20MB limit.');
      return;
    }
    setError(null);
    await handleUploadBase(files);
  }, [handleUploadBase]);

  // -- comments: the generic section, 3 audiences ---------------------------------------
  const { comments, addComment, deleteComment } = useEntityComments('warranty', warrantyId);
  const handleAddComment = async (content: string, audience?: string) => {
    await addComment(content, audience);
    onChanged?.(); // a comment can move the ball in court
  };
  const handleDeleteComment = async (commentId: string) => {
    await deleteComment(commentId);
    onChanged?.();
  };

  useEffect(() => {
    if (!isOpen || !companyId || clientView) return; // a client assigns nothing and cannot list the company's people
    setIsLoadingAssignmentOptions(true);
    fetchAssignableUsers()
      .then(setCompanyUsers)
      .finally(() => setIsLoadingAssignmentOptions(false));
  }, [isOpen, companyId, clientView]);

  useEffect(() => {
    if (isOpen && warrantyId) {
      fetchEntityAttachments('warranty', warrantyId).then(setUploadedFiles).catch((err) => console.error('Failed to load attachments:', err));
    } else {
      setUploadedFiles([]);
      resetPending();
    }
  }, [isOpen, warrantyId, setUploadedFiles, resetPending]);

  const initialValues = useMemo(() => {
    if (mode === 'edit' && initialData) {
      return {
        projectId: initialData.projectId || '',
        title: initialData.title || '',
        description: initialData.description || '',
        response: initialData.response || '',
        status: initialData.status || 'open',
        assignedTo: initialData.assignedTo || '',
      };
    }
    return {
      projectId: getDefaultProjectIdForNewRecord(selectedProject),
      title: '',
      description: '',
      response: '',
      status: 'open',
      assignedTo: '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData?.id, selectedProject]);

  const assignmentOptions = useMemo(
    () => companyUsers.map((u) => ({ value: u.id, label: u.company_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email })),
    [companyUsers]
  );

  // The response can only be written once there is a claim (a description).
  const canEditResponse = useMemo(() => {
    const description = formData.description ?? initialValues.description ?? '';
    return String(description).trim().length > 0;
  }, [formData.description, initialValues.description]);

  const warrantyFields = useMemo<GenericFormField[]>(() => clientView ? [
    { name: 'title', label: 'Title', type: 'text', required: true, row: 1, placeholder: 'Enter warranty title', disabled: mode === 'edit' },
    { name: 'projectId', label: 'Project', type: 'select', required: true, row: 1, options: projects.map((p) => ({ label: p.name, value: p.id })), placeholder: 'Select', disabled: mode === 'edit' },
    { name: 'description', label: 'Description', type: 'textarea', row: 2, colSpan: 2, placeholder: 'Describe the problem', disabled: mode === 'edit' },
  ] : [
    { name: 'title', label: 'Title', type: 'text', required: true, row: 1, placeholder: 'Enter warranty title' },
    { name: 'projectId', label: 'Project', type: 'select', required: true, row: 1, options: projects.map((p) => ({ label: p.name, value: p.id })), placeholder: 'Select' },
    { name: 'description', label: 'Description', type: 'textarea', row: 2, colSpan: 2, placeholder: 'Enter warranty description' },
    {
      name: 'response',
      label: 'Response',
      type: 'textarea',
      row: 3,
      colSpan: 2,
      disabled: !canEditResponse,
      placeholder: canEditResponse ? 'Enter response to the warranty claim' : 'Response field will be available after a claim is filed',
    },
    { name: 'status', label: 'Status', type: 'select', row: 4, options: STATUS_OPTIONS.map((s) => ({ label: s.label, value: s.value })), placeholder: 'Select' },
    { name: 'assignedTo', label: 'Assign To', type: 'select', row: 4, options: assignmentOptions, placeholder: 'Select', disabled: isLoadingAssignmentOptions, membershipProjectField: 'projectId' },
  ], [clientView, mode, projects, canEditResponse, assignmentOptions, isLoadingAssignmentOptions]);

  const handleFieldChange = (_name: string, _value: unknown, current: Record<string, any>) => setFormData(current);

  /** Saves (create or update) and uploads any staged files. Same validation as the Save button. */
  const handleSubmit = async (data: Record<string, any>, skipClose = false): Promise<Warranty | undefined> => {
    if (isSubmitting) return undefined;
    setError(null);
    const missing = firstMissingRequiredMessage(warrantyFields, data);
    if (missing) {
      setError(missing);
      return undefined;
    }

    setIsSubmitting(true);
    try {
      const payload: NewWarrantyInput = clientView ? {
        title: data.title.trim(),
        description: data.description?.trim() || undefined,
        projectId: data.projectId,
        status: 'open' as WarrantyStatus,
      } : {
        title: data.title.trim(),
        description: data.description?.trim() || undefined,
        response: data.response?.trim() || undefined,
        projectId: data.projectId,
        status: data.status as WarrantyStatus,
        assignedTo: data.assignedTo || undefined,
      };
      const result = await onSubmit(payload);
      if (result?.id && pendingFiles.length > 0) {
        const uploaded = await uploadPending(result.id);
        if (uploaded.length < pendingFiles.length) {
          setError(`The warranty was saved, but ${pendingFiles.length - uploaded.length} file(s) could not be uploaded.`);
        }
      }
      if (!skipClose) onCancel();
      return result;
    } catch (err: any) {
      setError(err?.message || 'Failed to save warranty');
      console.error('Error submitting warranty:', err);
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  };

  // -- unsaved changes ----------------------------------------------------------------------
  const initialValuesRef = useRef<Record<string, any>>({});
  const initializedWarrantyIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      initializedWarrantyIdRef.current = null;
      return;
    }
    const currentId = initialData?.id || null;
    if (initializedWarrantyIdRef.current !== currentId) {
      initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));
      setFormData(JSON.parse(JSON.stringify(initialValues)));
      initializedWarrantyIdRef.current = currentId;
    }
  }, [isOpen, initialData?.id, initialValues]);

  const hasFormChanged = (): boolean => {
    const current = formData;
    const initial = initialValuesRef.current;
    const keys = Array.from(new Set([...Object.keys(current), ...Object.keys(initial)]));
    const changed = keys.some((key) => String(current[key] ?? '').trim() !== String(initial[key] ?? '').trim());
    return changed || pendingFiles.length > 0;
  };

  const handleCancel = () => {
    if (hasFormChanged() && !window.confirm('Are you sure you want to lose all unsaved progress?')) return;
    onCancel();
  };

  // -- footer actions that need a saved warranty -----------------------------------------------
  // Both save first (the id / number is needed) using the SAME validation as Save.
  const saveThen = async (action: (saved: Warranty) => Promise<void> | void) => {
    const saved = mode === 'edit' && initialData?.id && !hasFormChanged()
      ? initialData
      : await handleSubmit(formData, true);
    if (!saved?.id) return; // validation / save error is already shown in the form
    await action(saved);
  };

  const customFooterButtons = clientView ? (
    <div className="flex gap-2">
      <StandardButton customVariant="white" onClick={handleCancel} type="button">{mode === 'create' ? 'Cancel' : 'Close'}</StandardButton>
      {mode === 'create' && <SecondaryButton type="submit" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Submit Claim'}</SecondaryButton>}
    </div>
  ) : (
    <div className="flex gap-2">
      <StandardButton customVariant="white" onClick={handleCancel} type="button">Cancel</StandardButton>
      {onCreatePO && (
        <StandardButton
          type="button"
          customVariant="white"
          disabled={isSubmitting}
          title="Create a purchase order for this warranty"
          onClick={() => saveThen((saved) => onCreatePO(saved, formData.projectId || saved.projectId))}
        >
          Create PO
        </StandardButton>
      )}
      {onSend && (
        <StandardButton
          type="button"
          customVariant="white"
          disabled={isSubmitting}
          title="Save and email this warranty (claim, response, status, latest file) to the project's client"
          onClick={() => saveThen(async (saved) => {
            try {
              await onSend(saved.id, saved);
            } catch (err: any) {
              console.error('Error sending warranty:', err);
              setError(err?.message || 'Failed to email the warranty');
            }
          })}
        >
          Email to Client
        </StandardButton>
      )}
      <SecondaryButton type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</SecondaryButton>
    </div>
  );

  const related = initialData?.relatedPurchaseOrders ?? [];

  return (
    <GenericForm
      title="Warranty"
      mode={mode}
      fields={warrantyFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isOpen={isOpen}
      onFieldChange={handleFieldChange}
      additionalSections={
        <>
          {mode === 'edit' && related.length > 0 && (
            <div className="grid gap-2">
              <div className="font-bold">Related Purchase Orders</div>
              <ul className="space-y-1 text-body">
                {related.map((po) => (
                  <li key={po.id} data-testid={`related-po-${po.id}`}>
                    <span className="font-medium">{po.poNumber}</span>{po.title ? ` — ${po.title}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!clientView && <GenericAttachmentsSection
            attachments={uploadedFiles}
            pendingFiles={pendingFiles}
            onUpload={handleUpload}
            onDelete={mode === 'edit' ? handleDelete : undefined}
            onRemovePending={handleRemovePending}
            projectId={formData.projectId || ''} mode={mode}
          />}
          {!(clientView && mode === 'create') && <GenericCommentsSection
            comments={comments}
            onAddComment={mode === 'edit' && warrantyId ? handleAddComment : async () => {}}
            onDeleteComment={mode === 'edit' ? handleDeleteComment : undefined}
            mode={mode}
            audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
            placeholder={mode === 'create' ? 'Comments are available after creating the warranty' : 'Add a comment...'}
          />}
        </>
      }
      error={error}
      submitButtonText="Save Changes"
      saving={isSubmitting}
      customFooterButtons={customFooterButtons}
      hasUnsavedChanges={hasFormChanged}
    />
  );
}
